import React, { ComponentType, useEffect, useRef, useState } from 'react'
import { Collapse, Stack } from 'react-bootstrap'
import { $getRoot, EditorState, Klass, LexicalEditor, LexicalNode, LexicalNodeReplacement } from 'lexical'
import classNames from 'classnames'

import { RichTextToolbarPlugin } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { WritableLexicalEditor } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { ReadOnlyLexicalEditor } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/ReadOnlyLexicalEditor/ReadOnlyLexicalEditor'
import { getHtmlContentFromState, getTextContentFromState } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'
import { LEXICAL_NODES } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'
import { HTML_TYPE, TEXT_TYPE } from '../../../common/copy'
import { AIAgentInteractionResult } from '../../../models/AIAgent'
import { Button } from '../../../components/Button/Button'

import './EditableAIAgentResult.scss'

export interface AIAgentResultState {
  html: string
  editorState: EditorState
  displayId: string
}

export interface EditButtonComponentProps {
  handleClick: () => void
}

interface EditableAIAgentResultProps {
  label?: string
  aiAgentResultState: AIAgentResultState
  handleInteraction: (editedResult: string) => Promise<AIAgentInteractionResult<any> | undefined>
  isLoading: boolean
  interactionIsLoading: boolean
  collapsible?: boolean
  collapseByDefault?: boolean
  ActionButtonsComponent?: ComponentType
  EditButtonComponent?: ComponentType<EditButtonComponentProps>
  lexicalNodes?: ReadonlyArray<Klass<LexicalNode> | LexicalNodeReplacement>
}

export const EditableAIAgentResult: React.FC<EditableAIAgentResultProps> = ({
  label = 'Agent Result',
  aiAgentResultState,
  handleInteraction,
  isLoading,
  interactionIsLoading,
  collapseByDefault = false,
  collapsible = false,
  ActionButtonsComponent,
  EditButtonComponent,
  lexicalNodes = LEXICAL_NODES,
}) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(collapsible ? collapseByDefault : false)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [editedResult, setEditedResult] = useState<string | null>(null)

  const handleEdit = () => {
    setEditedResult(aiAgentResultState.html)
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditedResult(null)
  }

  const handleSave = async () => {
    if (!editedResult) {
      return
    }
    const interactionResult = await handleInteraction(editedResult)
    if (interactionResult) {
      setIsEditing(false)
      setEditedResult(null)
    }
  }

  const handleChange = (editorState: EditorState) => {
    const editorContent = getEditorContent(editorState, lexicalNodes)
    setEditedResult(editorContent ? editorContent[HTML_TYPE] : null)
  }

  const DefaultEditButtonComponent: React.FC<EditButtonComponentProps> = ({ handleClick }) => (
    <Button className='button-large p-2' variant='primary' onClick={handleClick} disabled={isLoading || interactionIsLoading}>
      <i className='bi bi-pencil' />
      <span className='button-small'>Edit</span>
    </Button>
  )

  const EditButton = EditButtonComponent ?? DefaultEditButtonComponent

  return (
    <div className='border zocks-border-0.5 bg-white py-3'>
      {collapsible ? (
        <div className='d-flex justify-content-between align-items-center py-2 zocks-px-1.75 w-100' onClick={() => setIsCollapsed(!isCollapsed)}>
          <span className='lead-small'>{label}</span>
          <Stack direction='horizontal' gap={2}>
            <Stack
              direction='horizontal'
              gap={2}
              onClick={(event) => {
                event.stopPropagation()
              }}
            >
              {!isEditing ? <EditButton handleClick={handleEdit} /> : null}
              {ActionButtonsComponent ? <ActionButtonsComponent /> : null}
            </Stack>
            <i
              className={classNames('bi', {
                'bi-chevron-expand': isCollapsed,
                'bi-chevron-contract': !isCollapsed,
              })}
            />
          </Stack>
        </div>
      ) : null}
      <Collapse in={!isCollapsed} dimension={'height'}>
        <div>
          <div className='d-flex flex-column gap-3 zocks-px-1.75 '>
            <div className='d-flex gap-3 ignore-in-pdf'>
              {isEditing ? (
                <>
                  <Button className='button-small px-3 py-2' variant='secondary' onClick={handleCancel} disabled={interactionIsLoading}>
                    Cancel
                  </Button>
                  <Button className='button-small px-3 py-2' variant='primary' onClick={handleSave} disabled={!editedResult} loading={interactionIsLoading}>
                    Save
                  </Button>
                </>
              ) : null}
            </div>
            {isEditing ? (
              <div>
                <EditableAIAgentResultEditor
                  aiAgentResultState={aiAgentResultState}
                  handleChange={handleChange}
                  interactionIsLoading={interactionIsLoading}
                  lexicalNodes={lexicalNodes}
                />
              </div>
            ) : (
              <div className='zocks-pb-2.5'>
                <ReadOnlyLexicalEditor key={aiAgentResultState.displayId} initialEditorState={aiAgentResultState.editorState} lexicalNodes={lexicalNodes} />
              </div>
            )}
          </div>
        </div>
      </Collapse>
    </div>
  )
}

interface EditableAIAgentResultEditorProps {
  aiAgentResultState: AIAgentResultState
  handleChange: (editorState: EditorState, editor: LexicalEditor) => void
  interactionIsLoading: boolean
  lexicalNodes: ReadonlyArray<Klass<LexicalNode> | LexicalNodeReplacement>
}

const EditableAIAgentResultEditor: React.FC<EditableAIAgentResultEditorProps> = ({ aiAgentResultState, handleChange, interactionIsLoading, lexicalNodes }) => {
  const editorRef = useRef<LexicalEditor>(null)

  useEffect(() => {
    if (!editorRef.current) {
      return
    }
    const isEditable = editorRef.current.isEditable()
    if (!interactionIsLoading === isEditable) {
      return
    }
    editorRef.current.setEditable(!interactionIsLoading)
  }, [interactionIsLoading])

  return (
    <WritableLexicalEditor
      key={aiAgentResultState.displayId}
      ref={editorRef}
      onChange={handleChange}
      initialEditorState={aiAgentResultState.editorState}
      className='flex-column-reverse align-items-start'
      containerClassName='w-100'
      editorContainerClassName='zocks-py-0.75 px-2'
      lexicalNodes={lexicalNodes}
      header={
        <RichTextToolbarPlugin className='px-3 zocks-py-0.75 gap-2' disabled={interactionIsLoading}>
          <div className='editable-text-result-rich-text-divider' />
        </RichTextToolbarPlugin>
      }
    />
  )
}

const isEditorEmpty = (editorState: EditorState) => editorState.isEmpty() || editorState.read(() => $getRoot().isEmpty())

export const getEditorContent = (editorState: EditorState, nodes: ReadonlyArray<Klass<LexicalNode> | LexicalNodeReplacement> = LEXICAL_NODES) => {
  if (isEditorEmpty(editorState)) {
    return null
  }
  const lexicalState = JSON.stringify(editorState)
  return {
    [TEXT_TYPE]: getTextContentFromState(lexicalState, nodes),
    [HTML_TYPE]: getHtmlContentFromState(lexicalState, nodes),
  }
}
